import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { CommonService } from '../../../../services/common/common.service';
import { IntensitiesDataService, IntensitiesColumnsService } from '../../../services';
import { Intensity, IntensitiesResult } from '../../../interfaces';
import { DialogFilerComponent } from '../../filer/filer.component';

@Component({
  selector: 'app-intensity-form',
  templateUrl: './intensity-form.component.html',
  styleUrls: ['./intensity-form.component.scss'],
  providers: [MessageService, ConfirmationService, DynamicDialogRef, DynamicDialogConfig],
})
export class IntensityFormComponent implements OnInit, OnDestroy, AfterViewInit {

  public intensity: Intensity = {};
  public intensityForm: FormGroup = new FormGroup({});
  public intensities: Intensity[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public isLoading: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  public redirect = true;
  public disableSubmit = false;
  public enableFormAlert = true;

  private filerDialog: DynamicDialogRef = new DynamicDialogRef();

  constructor(
    private translate: TranslateService,
    public common: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private intensitiesDataService: IntensitiesDataService,
    public intensitiesColumnsService: IntensitiesColumnsService,
  ) {
  }

  ngOnInit(): void {
    console.log('ngOnInit', 'IntensityFormComponent');
    this.route.params.subscribe((params: any) => {
      if (typeof params['id'] !== 'undefined') {
        this.titleService.setTitle('Intensity - Edit');
        this.getIntensity(parseInt(params['id'], 10));
      }
    });
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.intensity !== 'undefined') {
      this.intensity = this.dynamicDialogConfig.data.intensity;
    }
    if (typeof this.intensity.id === 'undefined' || this.intensity.id === null) {
      this.intensity.id = null;
      this.intensity.isPublished = false;
    }
    this.getIntensities();
    this.prepareFrom();
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy', 'IntensityFormComponent');
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit', 'IntensityFormComponent');
  }

  getIntensity(id: number): void {
    this.isLoading = true;
    this.intensitiesDataService.getIntensity(id).subscribe((response: Intensity|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.intensity = response;
        this.prepareFrom();
      }
    });
  }

  getIntensities(): void {
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.intensities !== 'undefined' && this.dynamicDialogConfig.data.intensities.length > 0) {
      this.intensities = this.dynamicDialogConfig.data.intensities;
      return;
    }
    this.intensitiesDataService.getIntensities(this.parameters).subscribe((response: IntensitiesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.intensities = response.results;
      }
    });
  }

  prepareFrom(): void {
    this.intensityForm = new FormGroup({
      id: new FormControl(this.intensity.id, []),
      name: new FormControl(this.intensity.name, [Validators.required, ]),
      nameAr: new FormControl(this.intensity.nameAr, []),
      nameFr: new FormControl(this.intensity.nameFr, []),
      description: new FormControl(this.intensity.description, []),
      icon: new FormControl(this.intensity.icon, []),
      isPublished: new FormControl(this.intensity.isPublished, [Validators.required, ]),
    });
  }

  list() {
    this.router.navigate(['/intensities']);
  }

  showErrors() {
      this.showErrors();
      const invalidFields: string[] = [];
      Object.keys(this.intensityForm.controls).forEach((field: any) => {
        const control = this.intensityForm.get(field);
        if (control !== null) {
          control.markAsTouched({ onlySelf: true });
          if (control.invalid) {
            invalidFields.push(field);
          }
        }
      });
  }

  submitForm(redirect: boolean) {
    this.redirect = redirect;
    if (!this.intensityForm.valid) {
      this.showErrors();
    } else {
      this.saveData(redirect);
    }
  }

  saveData(redirect: boolean) {
    this.intensity = this.intensityForm.value;
    this.disableSubmit = true;
    console.log('intensity', this.intensity);
    if (typeof this.intensity.id !== 'undefined' && this.intensity.id !== null) {
      this.add(redirect);
    } else {
      this.update(redirect);
    }
    return false;
  }

  add(redirect: boolean) {
    this.intensitiesDataService.updateIntensity(this.intensity).subscribe((data: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('INTENSITYUPDATED')});
      if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
        this.dynamicDialogRef.close({refresh: true});
      } else if (redirect) {
        this.list();
      }
    }, (error: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('INTENSITYNOTUPDATED') + ' ' + error.message});
      console.warn(error);
    });
  }

  update(redirect: boolean) {
    this.intensitiesDataService.addIntensity(this.intensity).subscribe((data: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('INTENSITYCREATED')});
        if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
          this.dynamicDialogRef.close({refresh: true});
        } else if (redirect) {
          this.list();
        }
      }, (error: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('INTENSITYNOTCREATED') + ' ' + error.message});
        console.warn(error);
    });
  }

  openFilerDialog(field: string): void {
    let url = '';
    const control = this.intensityForm.get(field);
    if (control !== null) {
      url = control.value;
    }
    this.filerDialog = this.dialogService.open(DialogFilerComponent, {
      header: '', // this.translate.instant('FILEMANAGER'),
      width: '90%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1500,
      data: {
        path: 'intensities',
        field: field,
        url: url,
      },
    });
    this.filerDialog.onClose.subscribe((response: any) => {
      if (response && response.selected) {
        const control = this.intensityForm.get(field);
        if (control !== null) {
          control.setValue(response.selected.webPath);
        }
      }
    });
  }

}

@Component({
  selector: 'app-intensity-form',
  templateUrl: './intensity-form.component.html',
  styleUrls: ['./intensity-form.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class DialogIntensityFormComponent extends IntensityFormComponent {
}

